"use client";

import type { CmsSidebarNavigatorLinkFragment } from "@workspace/casino_core/queries/CmsSidebarQuery";
import CmsPhrase from "@workspace/cms-phrases/src/phrase/CmsPhrase";
import resolvePhrase from "@workspace/cms-phrases/src/phrase/resolvePhrase";
import { cn, SidebarMenuButton, SidebarMenuItem } from "@workspace/frontend-ui";
import LinkComponent from "@workspace/nextjs_utils/src/components/LinkComponent";
import { usePathname } from "next/navigation";
import type { FC } from "react";
import SidebarIcon from "../SidebarSection/SidebarIcon";
import { SIDEBAR_TOOLTIP_CLASS } from "../sidebarTooltip";

type Props = {
  brandSlug: string;
  navigatorLinks: CmsSidebarNavigatorLinkFragment[];
  lang: string;
};

const MAX_NAVIGATOR_LINKS = 3;

const SidebarSwitcher: FC<Props> = ({ brandSlug, navigatorLinks, lang }) => {
  const pathname = usePathname();
  const langPrefix = lang ? `/${lang}` : "";
  const localPath =
    langPrefix && pathname.startsWith(langPrefix)
      ? pathname.slice(langPrefix.length) || "/"
      : pathname;

  const matchedLink =
    navigatorLinks.reduce<CmsSidebarNavigatorLinkFragment | null>(
      (best, link) => {
        if (!link.href) return best;
        if (!localPath.startsWith(link.href)) return best;
        if (!best || !best.href || link.href.length > best.href.length)
          return link;
        return best;
      },
      null,
    );

  const displayLinks = navigatorLinks?.slice(0, MAX_NAVIGATOR_LINKS) || [];
  if (displayLinks.length === 0) return null;

  return (
    <div>
      {/* Expanded: horizontal tab row */}
      <div className="flex gap-0.5 rounded-lg bg-background border-border border p-1 group-data-[collapsible=icon]:hidden!">
        {displayLinks.map((link) => {
          const isActive = link === matchedLink;
          return (
            <LinkComponent
              key={link.slug}
              href={link.href ?? ""}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-0.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent-background text-sidebar-primary-foreground"
                  : "text-sidebar-foreground/70 hover:bg-white/10 hover:text-sidebar-foreground",
              )}
            >
              <CmsPhrase
                phrase={link.title ?? undefined}
                brandSlug={brandSlug}
              />
            </LinkComponent>
          );
        })}
      </div>

      {/* Collapsed: stacked icon buttons */}
      <div className="hidden flex-col gap-1 group-data-[collapsible=icon]:flex! border-primary rounded-lg">
        {displayLinks.map((link) => {
          const isActive = link === matchedLink;
          return (
            <SidebarMenuItem key={link.slug}>
              <SidebarMenuButton
                asChild
                isActive={isActive}
                tooltip={{
                  children: resolvePhrase(brandSlug, link.title ?? undefined),
                  className: SIDEBAR_TOOLTIP_CLASS,
                }}
                className="data-active:text-sidebar-primary hover:text-sidebar-primary"
              >
                <LinkComponent href={link.href ?? ""}>
                  <SidebarIcon icon={link.icon} hugeIcon={link.hugeIcon} />
                </LinkComponent>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </div>
    </div>
  );
};

export default SidebarSwitcher;
